import { useState, useCallback } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { NoteOut } from '@/services/noteGoApi/noteGoApiSchemas';
import NoteSearchBar from './components/NoteSearchBar';
import NotePreviewCard from './components/NotePreviewCard';
import VibeCodeResult from './components/VibeCodeResult';
import VibeWaveform from './components/VibeWaveform';
import GoldButton from '@/components/GoldButton/GoldButton';
import { useVibeCode } from './hooks/useVibeCode';

const VibeCodeExperience = () => {
  const [selectedNote, setSelectedNote] = useState<NoteOut | null>(null);
  const { result, isSubmitting, submit, reset } = useVibeCode();

  // DEV: ?demo=wave forces waveform visible for preview
  const demoWave = new URLSearchParams(window.location.search).get('demo') === 'wave';

  const handleSelectNote = useCallback(
    (note: NoteOut) => {
      setSelectedNote(note);
      reset();
    },
    [reset],
  );

  const handleDeselect = useCallback(() => {
    setSelectedNote(null);
    reset();
  }, [reset]);

  const handleSubmit = useCallback(() => {
    if (!selectedNote) return;
    submit(selectedNote.id);
  }, [selectedNote, submit]);

  return (
    <Box sx={{ py: 3, display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
      {/* Header */}
      <Box>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
          <AutoFixHighIcon sx={{ fontSize: 28, color: 'primary.main' }} />
          <Typography variant="h5" fontWeight={600}>
            Vibe Code
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Select a video note and describe what you want to build. We'll analyze your video and generate a detailed vibe
          coding prompt you can use with any AI coding tool.
        </Typography>
      </Box>

      {/* Search */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Step 1: Pick your video
        </Typography>
        {selectedNote ? (
          <NotePreviewCard note={selectedNote} onDeselect={handleDeselect} />
        ) : (
          <NoteSearchBar onSelect={handleSelectNote} />
        )}
      </Box>

      {/* Generate button */}
      {selectedNote && !result && (
        <Box>
          <GoldButton
            onClick={handleSubmit}
            disabled={isSubmitting}
            shimmer={!isSubmitting}
            size="large"
            sx={{ px: 4, py: 1.2 }}
          >
            {isSubmitting ? 'Feeling the Vibe...' : 'Feel the Vibe'}
          </GoldButton>
        </Box>
      )}

      {/* Waveform loading animation */}
      {(isSubmitting || demoWave) && (selectedNote || demoWave) && (
        <Box>
          <VibeWaveform active={isSubmitting || demoWave} />
        </Box>
      )}

      {/* Result */}
      {result && selectedNote && (
        <Box>
          <VibeCodeResult
            result={result}
            noteTitle={selectedNote.title || 'Untitled Note'}
            onRegenerate={handleSubmit}
            isSubmitting={isSubmitting}
          />
        </Box>
      )}

      {/* Empty state hint */}
      {!selectedNote && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 200,
          }}
        >
          <Stack alignItems="center" spacing={1.5}>
            <AutoFixHighIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Search for a video note above to get started
            </Typography>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default VibeCodeExperience;
